'use client';

import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiBriefcase, FiCalendar } from 'react-icons/fi';
import { useContactModal } from '../ContactModal/ContactModalContext';
import { SectionHeader, CtaBlock } from '@/shared/ui';
import { experienceMock, type ExperienceItem } from '@/shared/store/mocks';
import { useIsMobile } from '../ContactModal/useIsMobile';
import styles from './experience-section.module.scss';

gsap.registerPlugin(ScrollTrigger);

export default function ExperienceSection() {
    const { title, subtitle, experiences, ctaBlock } = experienceMock;
    const { openModal: openContactModal } = useContactModal();
    const isMobile = useIsMobile();
    const [flippedCards, setFlippedCards] = useState<number[]>([]);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<HTMLDivElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);

    const toggleFlip = (index: number) => {
        setFlippedCards(prev => {
            if (prev.includes(index)) return prev.filter(i => i !== index);
            if (isMobile) return [index];
            return [...prev, index];
        });
    };

    const handleFlipWrapClick = (e: React.MouseEvent, index: number) => {
        if (isMobile) {
            e.stopPropagation();
            toggleFlip(index);
        }
    };

    const handleSectionClick = () => {
        if (isMobile && flippedCards.length > 0) setFlippedCards([]);
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(
                titleRef.current,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 0.8, scrollTrigger: { trigger: titleRef.current, start: 'top 80%' } },
            );
            const items = itemsRef.current?.querySelectorAll(`.${styles.item}`);
            if (items?.length)
                gsap.fromTo(
                    items,
                    { opacity: 0, x: (i: number) => (i % 2 === 0 ? -50 : 50) },
                    {
                        opacity: 1,
                        x: 0,
                        duration: 0.6,
                        stagger: 0.1,
                        scrollTrigger: { trigger: itemsRef.current, start: 'top 85%' },
                    },
                );
            gsap.fromTo(
                ctaRef.current,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 0.8, scrollTrigger: { trigger: ctaRef.current, start: 'top 85%' } },
            );
        }, sectionRef);
        return () => ctx.revert();
    }, []);

    return (
        <section id="experience" ref={sectionRef} className={styles.section} onClick={handleSectionClick}>
            <div className={styles.container}>
                <SectionHeader
                    ref={titleRef}
                    title={title}
                    subtitle={subtitle}
                    classNames={{
                        root: styles.titleBlock,
                        title: styles.title,
                        line: styles.titleLine,
                        subtitle: styles.subtitle,
                    }}
                />
                <div className={styles.timeline}>
                    <div className={styles.line} />
                    <div ref={itemsRef} className={styles.items}>
                        {experiences.map((exp: ExperienceItem, index: number) => (
                            <div key={exp.year} className={styles.item} data-side={index % 2 === 0 ? 'left' : 'right'}>
                                <div
                                    className={styles.flipWrap}
                                    onClick={e => handleFlipWrapClick(e, index)}
                                    onMouseEnter={() => !isMobile && toggleFlip(index)}
                                    onMouseLeave={() => !isMobile && toggleFlip(index)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleFlip(index)}
                                    aria-label={
                                        isMobile
                                            ? `${exp.role} at ${exp.company}, click to read more`
                                            : `${exp.role} at ${exp.company}, hover to read more`
                                    }
                                >
                                    <div
                                        className={styles.flipInner}
                                        style={{
                                            transform: flippedCards.includes(index)
                                                ? 'rotateY(180deg)'
                                                : 'rotateY(0deg)',
                                        }}
                                    >
                                        <div className={styles.cardFront}>
                                            <div className={styles.cardHover} aria-hidden />
                                            <div className={styles.cardGlow} aria-hidden />
                                            <div className={styles.cardCorner} aria-hidden />
                                            <div className={styles.cardContent}>
                                                <span className={styles.yearBadge}>
                                                    <FiCalendar size={14} /> {exp.year}
                                                </span>
                                                <h3 className={styles.role}>{exp.role}</h3>
                                                <div className={styles.company}>
                                                    <FiBriefcase size={14} /> {exp.company}
                                                </div>
                                                <p className={styles.desc}>{exp.description}</p>
                                                <ul className={styles.achievements}>
                                                    {exp.achievements.map((a: string) => (
                                                        <li key={a}>{a}</li>
                                                    ))}
                                                </ul>
                                                <p className={styles.hint}>
                                                    {isMobile ? 'Click' : 'Hover to learn more'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className={styles.cardBack}>
                                            <div className={styles.cardBackBg} aria-hidden />
                                            <div className={styles.cardBackGlow} aria-hidden />
                                            <div className={styles.cardBackContent}>
                                                <div className={styles.backTitleWrap}>
                                                    <h3 className={styles.backTitle}>About This Role</h3>
                                                    <div className={styles.backTitleLine} />
                                                </div>
                                                <div className={styles.backDescScroll}>
                                                    <p className={styles.backDesc}>{exp.backDescription}</p>
                                                </div>
                                                <div className={styles.backDots}>
                                                    <span className={styles.backDot} />
                                                    <span className={styles.backDot} />
                                                    <span className={styles.backDot} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.timelineDot} aria-hidden>
                                    <span className={styles.timelineDotInner} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div id="experience-cta" ref={ctaRef}>
                    <CtaBlock
                        title={ctaBlock.title}
                        subtitle={ctaBlock.subtitle}
                        buttonLabel={ctaBlock.buttonLabel}
                        onButtonClick={() => openContactModal()}
                        classNames={{
                            root: styles.ctaBlock,
                            bg: styles.ctaBg,
                            inner: styles.ctaInner,
                            title: styles.ctaTitle,
                            text: styles.ctaSubtitle,
                            button: styles.ctaButton,
                        }}
                    />
                </div>
            </div>
        </section>
    );
}
